import { pctDisplayNumber } from './termSheetFormatters';

const COL_WIDTH = 1800;
const WORD_PROCESSING_NS =
  'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

export function detectWordXmlPrefix(documentXml) {
  if (documentXml.includes(`xmlns:ns0="${WORD_PROCESSING_NS}"`)) {
    return 'ns0';
  }
  return 'w';
}

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function tableCell(text, prefix, width = COL_WIDTH) {
  const safeText = escapeXml(text);
  return `<${prefix}:tc><${prefix}:tcPr><${prefix}:tcW ${prefix}:w="${width}" ${prefix}:type="dxa"/></${prefix}:tcPr><${prefix}:p><${prefix}:pPr><${prefix}:jc ${prefix}:val="both"/></${prefix}:pPr><${prefix}:r><${prefix}:rPr><${prefix}:rFonts ${prefix}:asciiTheme="minorBidi" ${prefix}:hAnsiTheme="minorBidi" ${prefix}:cstheme="minorBidi"/><${prefix}:sz ${prefix}:val="20"/><${prefix}:szCs ${prefix}:val="20"/></${prefix}:rPr><${prefix}:t xml:space="preserve">${safeText}</${prefix}:t></${prefix}:r></${prefix}:p></${prefix}:tc>`;
}

function tableRow(cells, prefix) {
  return `<${prefix}:tr>${cells.join('')}</${prefix}:tr>`;
}

export function sortedRoasThresholdRows(fields) {
  return [...(fields.roasThresholds ?? [])]
    .filter(
      (row) =>
        row.month !== '' &&
        row.month != null &&
        row.minRoasPct !== '' &&
        row.minRoasPct != null
    )
    .sort((left, right) => Number(left.month) - Number(right.month));
}

export function buildRoasCovenantTableModel(fields) {
  if (!fields.roasMinimumEnabled) {
    return null;
  }
  const rows = sortedRoasThresholdRows(fields);
  const momPct = pctDisplayNumber(fields.momDeltaPct);
  if (!rows.length && momPct == null) {
    return null;
  }
  const headers = rows.map((row) => `Month ${row.month}`);
  const values = rows.map((row) => {
    const pct = pctDisplayNumber(row.minRoasPct);
    return pct != null ? `${pct}%` : '[]%';
  });
  headers.push('MoM Delta');
  values.push(momPct != null ? `${momPct}%` : '[]%');
  return { headers, values };
}

export function buildRoasCovenantTableXml(fields, prefix = 'w') {
  const model = buildRoasCovenantTableModel(fields);
  if (!model) {
    return '';
  }
  const gridCols = model.headers
    .map(() => `<${prefix}:gridCol ${prefix}:w="${COL_WIDTH}"/>`)
    .join('');
  const headerRow = tableRow(
    model.headers.map((text) => tableCell(text, prefix)),
    prefix
  );
  const valueRow = tableRow(
    model.values.map((text) => tableCell(text, prefix)),
    prefix
  );
  return `<${prefix}:tbl><${prefix}:tblPr><${prefix}:tblStyle ${prefix}:val="TableGrid"/><${prefix}:tblW ${prefix}:w="0" ${prefix}:type="auto"/><${prefix}:tblInd ${prefix}:w="3711" ${prefix}:type="dxa"/><${prefix}:tblLook ${prefix}:val="04A0" ${prefix}:firstRow="1" ${prefix}:lastRow="0" ${prefix}:firstColumn="1" ${prefix}:lastColumn="0" ${prefix}:noHBand="0" ${prefix}:noVBand="1"/></${prefix}:tblPr><${prefix}:tblGrid>${gridCols}</${prefix}:tblGrid>${headerRow}${valueRow}</${prefix}:tbl>`;
}

export const ROAS_TABLE_PLACEHOLDER = '[[TS_ROAS_TABLE]]';

const ROAS_TABLE_PLACEHOLDER_PARAGRAPH =
  /<(?:w:|ns0:)p\b[^>]*>(?:(?!<\/(?:w:|ns0:)p>)[\s\S])*?\[\[TS_ROAS_TABLE\]\](?:(?!<\/(?:w:|ns0:)p>)[\s\S])*?<\/(?:w:|ns0:)p>/;

function buildSpacingParagraph(prefix, afterTwips = '480') {
  return `<${prefix}:p><${prefix}:pPr><${prefix}:spacing ${prefix}:after="${afterTwips}" ${prefix}:line="300" ${prefix}:lineRule="exact"/></${prefix}:pPr></${prefix}:p>`;
}

function replaceRoasTablePlaceholder(documentXml, tableXml, prefix) {
  if (!documentXml.includes(ROAS_TABLE_PLACEHOLDER)) {
    return documentXml;
  }
  const spacingParagraph = buildSpacingParagraph(prefix);
  if (ROAS_TABLE_PLACEHOLDER_PARAGRAPH.test(documentXml)) {
    ROAS_TABLE_PLACEHOLDER_PARAGRAPH.lastIndex = 0;
    if (tableXml) {
      return documentXml.replace(
        ROAS_TABLE_PLACEHOLDER_PARAGRAPH,
        `${tableXml}${spacingParagraph}`
      );
    }
    return documentXml.replace(ROAS_TABLE_PLACEHOLDER_PARAGRAPH, '');
  }
  if (tableXml) {
    return documentXml.split(ROAS_TABLE_PLACEHOLDER).join(`${tableXml}${spacingParagraph}`);
  }
  return documentXml.split(ROAS_TABLE_PLACEHOLDER).join('');
}

export function applyRoasTableToDocumentXml(documentXml, fields) {
  const prefix = detectWordXmlPrefix(documentXml);
  const tableXml = buildRoasCovenantTableXml(fields, prefix);
  let xml = documentXml;
  if (xml.includes(ROAS_TABLE_PLACEHOLDER)) {
    return replaceRoasTablePlaceholder(xml, tableXml, prefix);
  }
  return xml.replace(
    new RegExp(
      `<(?:w:|ns0:)tbl[\\s\\S]*?\\[\\[TS_ROAS_PCT_0\\]\\][\\s\\S]*?</(?:w:|ns0:)tbl>`
    ),
    tableXml ? `${tableXml}${buildSpacingParagraph(prefix)}` : ''
  );
}

export function cleanupRepaymentCellInlineTableText(documentXml) {
  return documentXml.replace(
    /tested cohorts\.Month 0Month \[\]Month \[\]MoM Delta\[\]%\[\]%\[\]%\[\]%/,
    'tested cohorts.'
  );
}
